// 1. React
import { memo, useState } from "react";
import { classNames } from "helpers";

// 2. Pomoćne funkcije
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";

// 3. Tipovi
import { VehicleInvoiceInfoProps } from "./types";
import { InvoiceRent } from "services/api/fleet/vehicles/invoices/types";
import { Errors } from "services/forms/validation/types";

// 4. API / Konstante / Transformacije
import {
  createFleetVehicleInvoiceRentAPI,
  getFleetVehicleInvoiceRentPdfPreviewAPI,
  updateFleetVehicleInvoiceRentAPI,
  fiscalizeInvoiceRentAPI,
} from "services/api/fleet/vehicles/invoices/api";

// 5. UI Komponente
import { Button } from "ui/components/Button/Button";
import { Alert } from "ui/components/Alert";
import { PdfViewer } from "ui/components/PdfViewer";
import Loader from "ui/components/Loader/Loader";

// 6. Validacija
import { FLEET_VEHICLE_INVOICE_RENT_EDIT_FORM_SCHEMA } from "./validation";

// 7. Lokalne komponente
import InvoiceHeader from "./components/InvoiceHeader/InvoiceHeader";
import InvoiceItems from "./components/InvoiceItems/InvoiceItems";
import InvoiceTotal from "./components/InvoiceTotal/InvoiceTotal";
import InvoiceNotes from "./components/InvoiceNotes/InvoiceNotes";


const VehicleInvoiceInfo: React.FC<VehicleInvoiceInfoProps> = (props) => {
  const { vehicleId, formValues, setFormValues, onSuccess, onClose, onFinish, onStatusHistoryChange, onSetParentLoading, onContractRentsLoaded, hasIntro, pdfUrl, setPdfUrl, showPdf, setShowPdf } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Izvedeno stanje: račun je kreiran ali još nije fiskaliziran
  const isCreatedNotFiscalized = formValues.id && formValues.invoiceStatusId !== "draft" && formValues.fiscalizationStatus !== "confirmed";

  // Spremi kao predložak ili ažuriraj postojeći račun, zatim prikaži PDF pregled
  const handleSaveDraft = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_INVOICE_RENT_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      const draftFormValues = formValues.id && formValues.invoiceStatusId !== "draft"
        ? { ...formValues }
        : { ...formValues, invoiceStatusId: "draft" as const };
      let result: InvoiceRent | undefined;

      // Vozilo dolazi ili s ekrana vozila, ili iz odabranog ugovora kad se račun otvara iz pretrage
      // računa — tamo prop ostaje prazan.
      const targetVehicleId = vehicleId || formValues.vehicleId;

      if (formValues.id) {
        result = await updateFleetVehicleInvoiceRentAPI(formValues.id, draftFormValues, t);
      } else if (targetVehicleId) {
        result = await createFleetVehicleInvoiceRentAPI(targetVehicleId, draftFormValues, t);
      }

      if (!result) {
        // Bez vozila se račun ne može spremiti; loader se mora ugasiti, inače ekran ostaje zavrtjeti.
        setIsLoading(false);
        return;
      }

      const { id: newId, items: newItems } = result;
      setFormValues(prev => ({ ...prev, id: newId, invoiceStatusId: result!.status?.id ?? "draft", items: newItems, fiscalizationRequired: result!.fiscalizationRequired ?? prev.fiscalizationRequired }));
      if (onStatusHistoryChange) onStatusHistoryChange(result.statusHistory ?? [], result.status?.id ?? null);
      setIsLoading(false);

      // Prikaži PDF pregled nakon spremanja
      const url = await getFleetVehicleInvoiceRentPdfPreviewAPI(draftFormValues);
      if (url) {
        setPdfUrl(url);
        setShowPdf(true);
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }
  };

  // Kreiraj račun iz predloška — mijenja status u "kreiran", prikazuje overlay uspjeha + PDF
  const handleCreateInvoice = async (e: any) => {
    e.preventDefault();

    if (!formValues.id) return;

    setIsLoading(true);

    try {
      const createdFormValues = { ...formValues, invoiceStatusId: "created" as const };
      const result = await updateFleetVehicleInvoiceRentAPI(formValues.id, createdFormValues, t);

      if (result) {
        setFormValues(prev => ({
          ...prev,
          invoiceStatusId: "created",
          fiscalizationRequired: result.fiscalizationRequired ?? prev.fiscalizationRequired,
        }));
        if (onStatusHistoryChange) onStatusHistoryChange(result.statusHistory ?? [], result.status?.id ?? null);
        setIsLoading(false);

        // Prikaži overlay uspješnog kreiranja
        if (onFinish) onFinish();

        // Odmah dohvati i prikaži PDF pregled (isti tok kao kod ugovora)
        const url = await getFleetVehicleInvoiceRentPdfPreviewAPI({ ...formValues, id: result.id });
        if (url) {
          setPdfUrl(url);
          setShowPdf(true);
        }
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }
  };

  // Fiskaliziraj račun — poziva fiskalni API za prijavu u poreznu upravu
  const handleFiscalize = async (e: any) => {
    e.preventDefault();

    if (!formValues.id) return;

    setIsLoading(true);

    try {
      const result = await fiscalizeInvoiceRentAPI(formValues.id, t);
      if (result) {
        setFormValues(prev => ({
          ...prev,
          fiscalizationStatus: "confirmed",
          fiscalizationJIR: result.jir,
          fiscalizationZKI: result.zki,
        }));
      }
    } catch (e) {
      // greška se obrađuje kroz API sloj
    }

    setIsLoading(false);
  };

  // Prikaži PDF u bočnom panelu
  const handlePreviewPdf = async (e: any) => {
    e.preventDefault();

    const url = await getFleetVehicleInvoiceRentPdfPreviewAPI(formValues);
    if (url) {
      setPdfUrl(url);
      setShowPdf(true);
    }
  }

  // Otvori PDF u novom prozoru za ispis
  const handlePrintPdf = async (e: any) => {
    e.preventDefault();

    const url = await getFleetVehicleInvoiceRentPdfPreviewAPI(formValues);
    if (url) {
      const printWindow = window.open(url, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      }
    }
  };

  // Storniraj račun
  const handleCancelInvoice = async (e: any) => {
    e.preventDefault();

    if (!formValues.id) return;

    setIsLoading(true);
    try {
      const cancelledFormValues = { ...formValues, invoiceStatusId: "cancelled" };
      await updateFleetVehicleInvoiceRentAPI(formValues.id, cancelledFormValues, t);
      if (onSuccess) onSuccess();
      onClose();
    } catch {
      // greška se prikazuje putem snackbara
    }
    setIsLoading(false);
  };

  const items = formValues.items.filter(i => !i.deleted);

  // Stavke imaju vlastitu poruku ispod tablice, pa se ovdje ne ponavljaju.
  const blockingMessages = Object.entries(errors)
    .filter(([field, message]) => field !== "items" && !!message)
    .map(([, message]) => message);

  return (
    <>
      {isLoading && (
        <div className="relative h-[calc(100vh-80px)]">
          <Loader isLoading={true}>{null}</Loader>
        </div>
      )}

      <div className={isLoading ? "hidden" : ""}>
          {/* Broj računa, datum i najmoprimac nemaju svoje polje na ekranu — bez ove poruke bi klik
              na spremanje samo pao na validaciji i djelovao kao da se ništa ne događa. */}
          {blockingMessages.length > 0 && (
            <div className="mb-3">
              <Alert
                variant="danger"
                title="Račun se ne može spremiti"
                message={blockingMessages.join(" · ")}
              />
            </div>
          )}

          {/* Odabir ugovora/vozila/najmoprimca + postavke računa */}
          <InvoiceHeader
            vehicleId={vehicleId}
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            setIsLoading={onSetParentLoading}
            onContractRentsLoaded={onContractRentsLoaded}
          />
          {formValues.contractRentId && (
            <>
              {/* Stavke računa i ukupni iznosi */}
              <div className="">
                <div className="px-2 px-4 py-2 text-sm font-medium transition-colors duration-200 border-b-2 border-sky-600 text-sky-600">Stavke računa</div>
                <div className="border-x border-b border-slate-300 rounded-b-md">
                  <InvoiceItems
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                  <InvoiceTotal
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                </div>
              </div>

              {/* Potvrda fiskalizacije — prikazuje ZKI/JIR kodove nakon uspješne fiskalizacije */}
              {formValues.fiscalizationStatus === "confirmed" && formValues.id && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm">
                  <div className="font-medium mb-2 text-green-700">Fiskalizirano</div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">ZKI:</span>
                    <span className="font-mono text-xs">{formValues.fiscalizationZKI}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-slate-500">JIR:</span>
                    <span className="font-mono text-xs">{formValues.fiscalizationJIR}</span>
                  </div>
                </div>
              )}
              {/* Greška fiskalizacije */}
              {formValues.fiscalizationStatus === "error" && formValues.id && (
                <div className="mt-4">
                  <Alert variant="danger" message={formValues.fiscalizationErrorMessage || "Greška pri fiskalizaciji"} />
                </div>
              )}

              {/* Napomena vidljiva najmoprimcu na računu */}
              <InvoiceNotes
                formValues={formValues}
                setFormValues={setFormValues}
              />

              {/* Validacija: potrebna je barem jedna stavka */}
              {errors && errors["items"] !== undefined && items.length === 0 ? (
                <Alert title="Važno!" message={errors["items"]} variant="danger" />
              ) : <></>}
            </>
          )}

          {/* Fiksni footer s akcijskim gumbima */}
          <div className={classNames(
            "px-4 py-3 fixed bottom-0 bg-slate-50 border border-t-slate-200 flex justify-between gap-3",
            hasIntro ? "right-0 w-1/2" : "left-0 w-full"
          )}>
            {/* Lijevo: zatvaranje, storniranje, PDF akcije */}
            <div className="flex gap-2">
              <Button
                variant="outlined"
                children={t("admin.close")}
                onClick={onClose}
              />
              {/* Storno — samo za ne-predložak račune */}
              {formValues.id && formValues.invoiceStatusId !== "draft" && (
                <Button
                  variant="remove"
                  children="Storniraj račun"
                  onClick={handleCancelInvoice}
                />
              )}
              {/* Prebacivanje prikaza PDF-a */}
              {formValues.id && !showPdf && (
                <Button
                  startIcon="/media/icons/search.svg"
                  variant="secondary"
                  children={t("admin.preview.pdf")}
                  onClick={handlePreviewPdf}
                />
              )}
              {/* Ispis — samo kad je PDF vidljiv */}
              {formValues.id && showPdf && (
                <Button
                  startIcon="/media/icons/print.svg"
                  variant="secondary"
                  children="Ispiši račun"
                  onClick={handlePrintPdf}
                />
              )}
            </div>
            {/* Desno: spremanje/kreiranje/fiskalizacija */}
            <div className="flex gap-2">
              {/* Novi račun — spremi kao predložak */}
              {!formValues.id && (
                <Button
                  variant="primary"
                  children="Spremi predložak"
                  onClick={handleSaveDraft}
                />
              )}
              {/* Predložak → Kreiran */}
              {formValues.id && formValues.invoiceStatusId === "draft" && (
                <Button
                  variant="primary"
                  children="Kreiraj račun"
                  onClick={handleCreateInvoice}
                />
              )}
              {/* Kreiran+ — spremi promjene */}
              {formValues.id && formValues.invoiceStatusId !== "draft" && (
                <Button
                  variant={isCreatedNotFiscalized && formValues.fiscalizationRequired ? "outlined" : "primary"}
                  children="Spremi račun"
                  onClick={handleSaveDraft}
                />
              )}
              {/* Fiskaliziraj — istaknut kad je kreiran ali još nije fiskaliziran */}
              {isCreatedNotFiscalized && formValues.fiscalizationRequired && (
                <Button
                  variant="primary"
                  children="Fiskaliziraj"
                  onClick={handleFiscalize}
                  startIcon="/media/icons/check.svg"
                />
              )}
            </div>
          </div>

          {/* Bočni panel za pregled PDF-a */}
          {pdfUrl && (
            <PdfViewer
              url={pdfUrl}
              show={showPdf}
            />
          )}
      </div>

    </>
  );
};

export default memo(VehicleInvoiceInfo);
